import { useEffect, useRef, useState } from 'react'
import type { ReactNode } from 'react'
import styled, { css } from 'styled-components'

import { theme } from '@/styles/theme'

import { Spacer } from '@/components/elements/Spacer'

type Props = {
  accordionTitle: string
  children: ReactNode
  contentsLength: number
  isDefaultOpen?: boolean
}

export const Accordion = ({
  accordionTitle,
  children,
  contentsLength,
  isDefaultOpen = false,
}: Props) => {
  const [isExpand, setIsExpand] = useState(isDefaultOpen)
  const [childHeight, setChildHeight] = useState(0)
  const childElement = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (childElement.current) {
      const height = childElement.current?.clientHeight
      setChildHeight(height)
    }
  }, [])

  const handleClick = () => {
    if (childElement.current) {
      setIsExpand(!isExpand)
    }
  }

  return (
    <>
      <AccordionTitle onClick={handleClick}>
        {contentsLength > 0 && <Icon isExpand={isExpand} />}
        <Title>{accordionTitle}</Title>
      </AccordionTitle>
      <Spacer h={{ sp: 10, pc: 18 }} />
      {contentsLength === 0 ? (
        <NoItemText>ただいま掲載すべき事項はございません。</NoItemText>
      ) : (
        <AccordionItems isExpand={isExpand} childHeight={childHeight}>
          <div ref={childElement}>{children}</div>
        </AccordionItems>
      )}
    </>
  )
}

const AccordionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  &:hover {
    cursor: pointer;

    > h4 {
      color: ${theme.colors.lightBlue};
    }
  }

  @media screen and (max-width: 1047px) {
    align-items: flex-start;
    gap: 6px;
  }
`

const Title = styled.h4`
  font-size: 24px;
  font-weight: bold;
  color: ${theme.colors.trialBlue};
  line-height: 33.6px;

  @media screen and (max-width: 1047px) {
    font-size: 18px;
    line-height: 23.4px;
  }
`

const NoItemText = styled.span`
  font-size: 16px;
  line-height: 25.6px;
  color: ${theme.colors.textTitle};

  @media screen and (max-width: 1047px) {
    font-size: 15px;
    line-height: 24px;
  }
`

const AccordionItems = styled.div<{ isExpand: boolean; childHeight: number }>`
  height: ${({ isExpand, childHeight }) =>
    isExpand ? `${childHeight}px` : '0px'};
  opacity: ${({ isExpand }) => (isExpand ? 1 : 0)};

  transition: height 0.2s linear, opacity 0.5s ease-in;
  overflow: hidden;
`

const Icon = styled.span<{ isExpand: boolean }>`
  transition: all 0.25s ease;
  overflow: hidden;
  position: relative;
  padding-top: 2px;
  padding-right: 10px;
  width: 40px;
  height: 40px;

  &::before,
  &::after {
    content: '';
    display: inline-block;
    width: 30px;
    height: 4px;
    position: absolute;
    top: 50%;
    background-image: url('/images/accordion.png');
  }
  &::after {
    transform: rotate(90deg);
    transition: transform 0.4s ease;
  }

  ${({ isExpand }) =>
    isExpand
      ? css`
          &::after {
            transform: rotate(0);
          }
        `
      : ''}

  @media screen and (max-width: 1047px) {
    padding-top: 7px;
    padding-right: 16px;
    width: 16px;
    height: 23px;

    &::before,
    &::after {
      width: 16px;
      height: 2.13px;
    }
  }
`
